import axios from 'axios';

// {
//   "uniqueBotId": string,
//   "intent": string,
//   "message": string,
//   "contents": {boxes}
// }

export interface ConceptTaskSummaryRessources {
  /** List of URLs used */
  search: string[];
}

export interface ConceptTaskSummary {
  /** The resources used */
  ressources: ConceptTaskSummaryRessources;
  /** A summarized article. */
  article: string;
  /** Flag to indicate if there is no information. */
  no_information: boolean;
  /** List of source URLs. */
  sources: string[];
}

export interface ConceptTask {
  /** The MongoDB ID of this concept task */
  _id: string;
  /** List of websites. */
  websites: string[];
  /** List of concepts, or at least an empty array */
  concepts: ConceptData[];
  /** The ID of the bot */
  botId: string;
  /** Description of the generation */
  description: string;
  /** The intent it was created for */
  intent: string;
  /** Free-form information text provided for the generation */
  information: string;
  /** Status of the task (not indicative of the concepts or conversations). */
  status: string;
  /** The creation date/time. */
  createdAt: string;
  /** The date/time of last update. */
  updatedAt: string;
  /** Mongoose version number for the document */
  __v: number;
  /** The summary object. */
  summary: ConceptTaskSummary;
  /** List of conversations. */
  conversations: any[];
}

export interface ConceptData {
  /** The MongoDB ID of the concept */
  _id: string;
  /** Key points that make up this concept */
  bulletpoints: string[];
  /** The ID of the bot */
  botId: string;
  /** Display title for this concept */
  title: string;
  /** Longer explanation of the concept */
  description: string;
  /** The intent this was created for */
  intent: string;
  /** The ID of the concept task */
  conceptTask: string;
  /** The creation date/time. */
  createdAt: string;
  /** The date/time of last update. */
  updatedAt: string;
  /** Mongoose version number for the document */
  __v: number;
  /** ID of the conversation generated from this concept, if any */
  conversationId?: string | null;
}

export interface ConversationExtendedContext {
  /** List of source URLs backing the conversation */
  sources: string[];
  /** The resources used */
  ressources: ConceptTaskSummaryRessources;
}

export interface ConversationDataUser {
  /** Feature flags enabled for this user */
  features: string[];
  /** Channels the user is blocked from */
  blockedChannels: string[];
  /** Login/username of the hub user */
  username: string;
  /** UI language of the user */
  language: string;
  /** JWT issued-at timestamp (seconds since epoch) */
  iat: number;
  /** JWT expiry timestamp (seconds since epoch) */
  exp: number;
  /** JWT audience claim */
  aud: string;
}

export interface ConversationData {
  /** The MongoDB ID of the conversation */
  _id: string;
  /** Message history of the conversation */
  history: any[];
  /** The ID of the bot */
  botId: string;
  /** The intent this conversation belongs to */
  intent: string;
  /** Prompt provided by the user */
  userPrompt: string;
  /** System prompt used for generation */
  systemPrompt: string;
  /** Processing state of the conversation */
  state: string;
  /** Additional context (sources/resources) used during generation */
  extendedContext: ConversationExtendedContext;
  /** Decoded JWT info about the requesting user */
  user: ConversationDataUser;
  /** Generated content boxes */
  contents: any[];
  /** Populated when conversation is completed */
  assistant?: string | null;
  /** Populated when conversation is completed */
  json?: Record<string, any>[] | null;
  /** Populated when conversation is completed */
  output?: string | null;
  /** The creation date/time. */
  createdAt: string;
  /** The date/time of last update. */
  updatedAt: string;
  /** Mongoose version number for the document */
  __v: number;
}

// Variants with the conversation nested inside the concept (populated client-side
// by getFullConceptTask); otherwise identical to ConceptData/ConceptTask.
export interface NestedConceptData extends Omit<ConceptData, 'conversationId'> {
  /** ID of the conversation generated from this concept, if any */
  conversationId?: string | null;
  /**
   * Undefined if conversation generation has not begun.
   * Conversation object if partially done (running) or completed.
   */
  conversation?: ConversationData | null;
}

export interface NestedConceptTaskData extends Omit<ConceptTask, 'concepts'> {
  /** List of concepts (with their conversation attached), or at least an empty array */
  concepts: NestedConceptData[];
}

/** Extra-long request timeout (10 min) — assistant generation endpoints can be slow. */
const timeoutAmount = 600000;

export const AssistantService = {
  async startConversation({ uniqueBotId, intent, message, contents, signal, channel }: {
    /** Staging or live bot ID */
    uniqueBotId: string;
    /** Intent the conversation is about */
    intent: string;
    /** The user's message that starts the conversation */
    message: string;
    /** Content boxes providing context for the assistant */
    contents: any;
    /** Abort signal — when aborted before the response arrives, the result is discarded (`false` is returned) */
    signal: AbortSignal;
    /** Channel ID the content belongs to */
    channel?: string | null;
  }): Promise<any> {
    try {
      const data = {
        uniqueBotId,
        intent,
        message,
        contents,
        channel,
      };

      const res = await axios.post('/schaltzentrale/assistant/create', data, {
        timeout: timeoutAmount,
      });

      if (signal.aborted) {
        return false;
      }

      if (!res.data?.conversation) {
        return {
          error: 'No conversation in response?!',
        };
      }

      return res.data;
    } catch (error) {
      console.error('startConversation', error);
      return {
        error,
      };
    }
  },
  async continueConversation({ uniqueBotId, conversationId, message, signal }: {
    /** Staging or live bot ID */
    uniqueBotId: string;
    /** ID of the conversation to continue */
    conversationId: string;
    /** The user's follow-up message */
    message: string;
    /** Abort signal — when aborted before the response arrives, the result is discarded (`false` is returned) */
    signal: AbortSignal;
  }): Promise<any> {
    try {
      const data = {
        uniqueBotId,
        conversationId,
        message,
      };

      const res = await axios.post('/schaltzentrale/assistant/continue', data, {
        timeout: timeoutAmount,
      });

      if (signal.aborted) {
        return false;
      }


      return res.data;
    } catch (error) {
      console.error('continueConversation', error);
      return {
        error,
      };
    }
  },
  async getConversation({ uniqueBotId, conversationId }: {
    /** Staging or live bot ID */
    uniqueBotId: string;
    /** ID of the conversation to fetch */
    conversationId: string;
  }): Promise<any> {
    try {
      const res = await axios.get(`/schaltzentrale/assistant/get/${conversationId}/${uniqueBotId}`, {
        timeout: timeoutAmount,
      });

      return res.data;
    } catch (error) {
      console.error('getConversation', error);
      return {
        error,
      };
    }
  },
  async generateAnswers({ uniqueBotId, intent, channel, conceptIds }: {
    /** Staging or live bot ID */
    uniqueBotId: string;
    /** Intent to generate answers for */
    intent: string;
    /** Channel ID the answers should target */
    channel?: string | null;
    /** IDs of the concepts to generate answers from */
    conceptIds: string[];
  }): Promise<any> {
    try {
      const data = {
        uniqueBotId,
        intent,
        channel,
        conceptIds,
      };

      const res = await axios.post('/schaltzentrale/assistant/generate/answers', data, {
        timeout: timeoutAmount,
      });

      return res.data;
    } catch (error) {
      return {
        error,
      };
    }
  },
  async generateConcepts({ uniqueBotId, intent, channel, websites, informations }: {
    /** Staging or live bot ID */
    uniqueBotId: string;
    /** Intent to generate concepts for */
    intent: string;
    /** Channel ID the concepts should target */
    channel?: string | null;
    /** Website URLs to source information from */
    websites: string[];
    /** Free-form information text to source from */
    informations?: string;
  }): Promise<any> {
    try {
      const data = {
        uniqueBotId,
        intent,
        channel,
        websites,
        informations,
      };

      const res = await axios.post('/schaltzentrale/assistant/generate/concepts', data, {
        timeout: timeoutAmount,
      });

      return res.data;
    } catch (error) {
      console.error('generateConcepts', error);
      return {
        error,
      };
    }
  },
  async getConceptTask({ uniqueBotId, taskId }: {
    /** Staging or live bot ID */
    uniqueBotId: string;
    /** ID of the concept task to fetch */
    taskId: string;
  }): Promise<any> {
    try {
      const res = await axios.get(`/schaltzentrale/assistant/concepts/getTask/${taskId}/${uniqueBotId}`, {
        timeout: timeoutAmount,
      });

      return res.data;
    } catch (error) {
      console.error('getConceptTask', error);
      return false;
    }
  },
  async getConcept({ uniqueBotId, conceptId }: {
    /** Staging or live bot ID */
    uniqueBotId: string;
    /** ID of the concept to fetch */
    conceptId: string;
  }): Promise<any> {
    try {
      const res = await axios.get(`/schaltzentrale/assistant/concepts/get/${conceptId}/${uniqueBotId}`, {
        timeout: timeoutAmount,
      });

      return res.data;
    } catch (error) {
      console.error('getConcept', error);
      return false;
    }
  },
  /**
   * Retrieves all possible information, except the box contents, for a given concept task.
   * @returns The task with conversations merged into their concepts;
   * `null` when the API has no task, `false` on error.
   */
  async getFullConceptTask({ uniqueBotId, intent, channelId }: {
    /** Staging or live bot ID */
    uniqueBotId: string;
    /** Intent the concept task was created for */
    intent: string;
    /** Optional channel ID to scope the lookup */
    channelId?: string | null;
  }): Promise<NestedConceptTaskData | null | false> {
    try {
      let url = `/schaltzentrale/assistant/concepts/getFull/${intent}/${uniqueBotId}`;
      if (channelId) url += `/${channelId}`;

      const res = await axios.get(url, {
        timeout: timeoutAmount,
      });
      if (!res.data?.task) return null;

      for (const concept of res.data.task?.concepts) {
        const found = res.data.task.conversations.find((c: any) => c._id === concept.conversation);
        if (!found) continue;

        concept.conversation = found;
      }

      return res.data.task;
    } catch (error) {
      console.error('getFullConceptTask', error);
      return false;
    }
  }
};
